import React, {
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters, AdminFilterValues, AdminFiltersSlotRenderArgs } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    ConfigChangeLogListLanguage,
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { buildAdminListRowKey } from '../../../Utils/adminListRowKeys'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { getChangeLogList } from '../../../../React/Common/API/ItemsGetters'
import type {
    ConfigChangeLogListItem,
    ConfigChangeLogListResponse,
    ConfigChangeLogPagination
} from '../../../../NonReact/Types/ConfigChangeLogListTypes'
import { COMMA, EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { ALL_VALUE } from '../../../../React/Common/Constants/DefaultValues'
import {
    CONFIG_CHANGE_LOG_ACTION_DESCRIPTION_ALL_KEY,
    CONFIG_CHANGE_LOG_ACTION_DESCRIPTION_KEY_BY_VALUE,
    CONFIG_CHANGE_LOG_ACTION_VALUES,
    CONFIG_CHANGE_LOG_DEFAULT_SORT,
    CONFIG_CHANGE_LOG_ENTITY_TYPE_VALUES,
    CONFIG_CHANGE_LOG_PAGE_CHANGE_DIRECTION_NEXT,
    CONFIG_CHANGE_LOG_PAGE_CHANGE_DIRECTION_PREV,
    CONFIG_CHANGE_LOG_TYPE_DESCRIPTION_ALL_KEY,
    CONFIG_CHANGE_LOG_TYPE_DESCRIPTION_KEY_BY_VALUE,
    configChangeLogTelSysOrPartitionDisplay,
    formatChangeLogUntilForApi
} from './ConfigChangeLogListConstants'
import { ChangeLogListQueryParams } from '../../../../NonReact/constants/ApiConstants'
import { hasResolvedInitialAdminScopeFilters } from '../../../Utils/adminListFilterReadiness'
import { ConfigChangeLogDetailModal } from './ConfigChangeLogDetailModal'
import { ConfigChangeLogListAdditionalFilters } from './ConfigChangeLogListAdditionalFilters'

/**
 * Table row model for {@link DataTable}; values are display strings / keys only so the row satisfies
 * {@code T extends { key: Key; Description: string; [key: string]: Key }}.
 */
type ConfigChangeLogRow = {
    key: React.Key
    entryId: string
    Description: string
    DateTime: string
    User: string
    Action: string
    Type: string
    CMEnvironment: string
    TelSys: string
    Partition: string
}

type LastResponseMeta = {
    readonly firstKey?: string
    readonly lastKey?: string
    readonly resultsSize: number
    readonly pageSize: number
}

/** Stable empty selection when {@link AdminFilterValues#changeLogActions} / {@code changeLogTypes} are unset. */
const NO_CHANGE_LOG_MULTI_FILTER_SELECTION: readonly string[] = []

/**
 * <p>Reads {@link ConfigChangeLogPagination#HasMore} when the server sends it.</p>
 *
 * @param raw - Raw {@code HasMore} value from JSON.
 * @returns {@code true} / {@code false} when the property is present and recognised, otherwise {@code undefined} (legacy responses).
 */
const parseChangeLogHasMoreFlag = (raw: unknown): Definable<boolean> => {
    if (typeof raw === 'string') {
        const normalised: string = raw.trim().toLowerCase()

        if (normalised === 'true') return true

        if (normalised === 'false') return false

        return undefined
    }

    if (raw === true) return true

    if (raw === false) return false

    return undefined
}

const PAGINATION_HAS_MORE_KEYS: readonly (keyof ConfigChangeLogPagination)[] = ['HasMore', 'hasMore']

/**
 * <p>Returns the first defined {@code HasMore} / {@code hasMore} value from the pagination object.</p>
 *
 * @param paginationBlock - {@link ConfigChangeLogListResponse} pagination block, if any.
 */
const readChangeLogPaginationHasMoreRaw = (paginationBlock: unknown): unknown => {
    if (IsVarEmpty(paginationBlock) || typeof paginationBlock !== 'object') {
        return undefined
    }

    const record: ConfigChangeLogPagination = paginationBlock as ConfigChangeLogPagination
    return PAGINATION_HAS_MORE_KEYS.map((k: keyof ConfigChangeLogPagination): unknown => record[k]).find(
        (v: unknown): boolean => isVarNotEmpty(v)
    )
}

const PAGINATION_TOTAL_COUNT_KEYS: readonly (keyof ConfigChangeLogPagination)[] = ['TotalCount', 'totalCount']

/**
 * <p>Returns the first defined {@code TotalCount} / {@code totalCount} value from the pagination object.</p>
 *
 * @param paginationBlock - Pagination block from the list response, if any.
 */
const readChangeLogPaginationTotalCountRaw = (paginationBlock: unknown): unknown => {
    if (IsVarEmpty(paginationBlock) || typeof paginationBlock !== 'object') {
        return undefined
    }

    const record: ConfigChangeLogPagination = paginationBlock as ConfigChangeLogPagination
    return PAGINATION_TOTAL_COUNT_KEYS.map((k: keyof ConfigChangeLogPagination): unknown => record[k]).find(
        (v: unknown): boolean => isVarNotEmpty(v)
    )
}

/**
 * <p>Parses a non-negative integer total from API pagination fields.</p>
 *
 * @param raw - Raw count value from JSON.
 * @returns A finite integer {@code >= 0}, or {@code undefined} when absent or invalid.
 */
const parseNonNegativePaginationCount = (raw: unknown): Definable<number> => {
    if (IsVarEmpty(raw)) {
        return undefined
    }

    let rawStr: string = EMPTY_STRING

    if (typeof raw === 'string') {
        rawStr = raw
    } else if (typeof raw === 'number') {
        rawStr = String(raw)
    }
    const n: number = typeof raw === 'number' ? raw : Number(rawStr.trim())

    if (!Number.isFinite(n) || n < 0) {
        return undefined
    }

    return Math.floor(n)
}

/**
 * Maps DataTable server sort tokens ({@code Field:ASC|DESC}) to change-log REST {@code sort} values
 * ({@code -Field} for descending per {@link CReqRespPaginationBase}).
 *
 * @param sortToken - Sort token from the data table, or {@code undefined} for default.
 * @returns Sort string accepted by {@code GET /v1/admin/changeLog}.
 */
const mapSortTokenToChangeLogApi = (sortToken: Definable<string>): string => {
    if (IsVarEmpty(sortToken)) {
        return CONFIG_CHANGE_LOG_DEFAULT_SORT
    }

    const [fieldRaw, directionRaw] = sortToken.split(':')
    const field: string = fieldRaw ?? EMPTY_STRING
    const direction: string = (directionRaw ?? 'ASC').toUpperCase()
    const descending: boolean = direction === 'DESC'

    if (field === 'DateTime') return descending ? '-DateTime' : 'DateTime'

    if (field === 'EntryID') return descending ? '-EntryID' : 'EntryID'

    return CONFIG_CHANGE_LOG_DEFAULT_SORT
}

const isConcreteTelSysOrPartitionFilterValue = (value: unknown): boolean =>
    isVarNotEmpty(value) && value !== ALL_VALUE && value !== EMPTY_STRING

/**
 * Routed audit trail (configuration change log) list view.
 *
 * @returns Audit trail list page content.
 */
export const ConfigChangeLogList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)
    language.loadBundle(ConfigChangeLogListLanguage.NAME)

    const [rows, setRows]: ReactState<ConfigChangeLogRow[]> = useState<ConfigChangeLogRow[]>([])
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)
    const [hasCompletedInitialListFetch, setHasCompletedInitialListFetch]: ReactState<boolean> =
        useState<boolean>(false)
    const [detailOpen, setDetailOpen]: ReactState<boolean> = useState<boolean>(false)
    const [detailRow, setDetailRow]: ReactState<OnlyNullable<ConfigChangeLogListItem>> =
        useState<OnlyNullable<ConfigChangeLogListItem>>(null)

    const lastMetaRef: MutableRefObject<OnlyNullable<LastResponseMeta>> = useRef<OnlyNullable<LastResponseMeta>>(null)
    const prevPageIdxRef: MutableRefObject<number> = useRef<number>(0)
    const itemsByEntryIdRef: MutableRefObject<Map<string, ConfigChangeLogListItem>> = useRef<
        Map<string, ConfigChangeLogListItem>
    >(new Map())

    const listCmeId: Nullable<string> = (filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID) as Nullable<string>
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listTelSysId: Definable<string | number> = isConcreteTelSysOrPartitionFilterValue(
        filters?.telephonyServer?.value
    )
        ? (filters?.telephonyServer?.value)
        : undefined
    const listPartitionId: Definable<string | number> =
        isVarNotEmpty(listTelSysId) && isConcreteTelSysOrPartitionFilterValue(filters?.partition?.value)
            ? (filters?.partition?.value)
            : undefined

    const listTelSysIdsParam: Definable<string> = isVarNotEmpty(listTelSysId) ? String(listTelSysId) : undefined
    const listPartitionIdsParam: Definable<string> = isVarNotEmpty(listPartitionId)
        ? String(listPartitionId)
        : undefined

    const listBefore: string = filters?.changeLogBefore ?? EMPTY_STRING

    const changeLogActions: readonly string[] = filters?.changeLogActions ?? NO_CHANGE_LOG_MULTI_FILTER_SELECTION
    const includeActionsKey: string = useMemo(
        (): string =>
            [...changeLogActions].sort((left: string, right: string): number => left.localeCompare(right)).join(COMMA),
        [changeLogActions]
    )

    const changeLogTypes: readonly string[] = filters?.changeLogTypes ?? NO_CHANGE_LOG_MULTI_FILTER_SELECTION
    const includeTypesKey: string = useMemo(
        (): string =>
            [...changeLogTypes].sort((left: string, right: string): number => left.localeCompare(right)).join(COMMA),
        [changeLogTypes]
    )

    const searchTrim: string = useMemo((): string => (filters?.search ?? EMPTY_STRING).trim(), [filters?.search])

    const suppressNulls: boolean = filters?.changeLogSuppressNulls === true

    useAdminListLegacyServletSwitch(ServletCmds.AUDITTRAIL, filters, userInfo?.Scope?.CMEnvironmentID)

    const getLogList = useCallback((): void => {
        if (!hasReceivedInitialFilters) return

        if (IsVarEmpty(listCmeId)) {
            setHasCompletedInitialListFetch(true)

            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        const [pageIdx, rowsPerPage]: PaginationInfo = pagination
        const prevIdx: number = prevPageIdxRef.current

        if (pageIdx > 0 && IsVarEmpty(lastMetaRef.current)) {
            setLoading((currentLoading: number): number => currentLoading - 1)
            setPagination([0, rowsPerPage])
            setHasCompletedInitialListFetch(true)

            return
        }

        let pageChangeFromKey: Definable<string>
        let pageChangeDirection: Definable<string>

        if (pageIdx > 0 && isVarNotEmpty(lastMetaRef.current)) {
            if (pageIdx > prevIdx) {
                pageChangeFromKey = lastMetaRef.current.lastKey
                pageChangeDirection = CONFIG_CHANGE_LOG_PAGE_CHANGE_DIRECTION_NEXT
            } else if (pageIdx < prevIdx) {
                pageChangeFromKey = lastMetaRef.current.firstKey
                pageChangeDirection = CONFIG_CHANGE_LOG_PAGE_CHANGE_DIRECTION_PREV
            }
        }

        const selectedActions: readonly string[] = filters?.changeLogActions ?? []
        const selectedEntityTypes: readonly string[] = filters?.changeLogTypes ?? []

        const includeParam: Definable<string> =
            selectedActions.length > 0
                ? selectedActions.map((a: string): string => a.toUpperCase()).join(COMMA)
                : undefined
        const typeParam: Definable<string> =
            selectedEntityTypes.length > 0 ? selectedEntityTypes.join(COMMA) : undefined

        const untilFormatted: string = formatChangeLogUntilForApi(listBefore)
        const untilParam: Definable<string> = isStringNotEmpty(untilFormatted) ? untilFormatted : undefined

        const query: ChangeLogListQueryParams = {
            CMEnvironmentID: listCmeId,
            RecursiveCMEnvironmentMatching: listIncludeRecursive ? undefined : false,
            telSysIDs: listTelSysIdsParam,
            partitionIDs: listPartitionIdsParam,
            until: untilParam,
            userID: isStringNotEmpty(searchTrim) ? searchTrim : undefined,
            include: includeParam,
            type: typeParam,
            pageSize: rowsPerPage,
            sort: mapSortTokenToChangeLogApi(sort),
            pageChangeFromKey,
            pageChangeDirection,
            deNull: suppressNulls ? true : undefined
        }

        getChangeLogList(query)
            .then((response: Nullable<ConfigChangeLogListResponse>): void => {
                if (!isMounted.current) return

                const items: readonly ConfigChangeLogListItem[] = response?.Data?.Items ?? []
                const paginationBlock: Definable<ConfigChangeLogPagination> = response?.Data?.Pagination
                const resultsSize: number = Number(paginationBlock?.ResultsSize ?? items.length)
                const pageSize: number = Number(paginationBlock?.PageSize ?? rowsPerPage)
                const hasMoreFlag: Definable<boolean> = parseChangeLogHasMoreFlag(
                    readChangeLogPaginationHasMoreRaw(paginationBlock)
                )
                const hasMore: boolean = isVarNotEmpty(hasMoreFlag)
                    ? hasMoreFlag
                    : resultsSize === pageSize && resultsSize > 0

                const rowsShownFloor: number = pageIdx * rowsPerPage + resultsSize
                const grandTotal: Definable<number> = parseNonNegativePaginationCount(
                    readChangeLogPaginationTotalCountRaw(paginationBlock)
                )

                let resolvedTotal: number = rowsShownFloor

                if (isVarNotEmpty(grandTotal)) {
                    resolvedTotal = grandTotal
                } else if (hasMore) {
                    resolvedTotal = -1
                }

                setTotalCount(resolvedTotal)

                lastMetaRef.current = {
                    firstKey: paginationBlock?.FirstKey,
                    lastKey: paginationBlock?.LastKey,
                    resultsSize,
                    pageSize
                }
                prevPageIdxRef.current = pageIdx

                const nextById: Map<string, ConfigChangeLogListItem> = new Map()
                const noneScopeLabel: string = language.get(
                    GenericAdminFilterLanguage.GenericAdminFilterLanguage_OPTION_NONE_LABEL
                )

                const mapped: ConfigChangeLogRow[] = items.map(
                    (item: ConfigChangeLogListItem, index: number): ConfigChangeLogRow => {
                        const entryId: string = item.EntryID ?? EMPTY_STRING

                        if (isStringNotEmpty(entryId)) {
                            nextById.set(entryId, item)
                        }

                        const telSysRaw: string =
                            item.TelephoneSystemDescriptions ?? item.TelephoneSystemIDs ?? EMPTY_STRING
                        const partitionRaw: string = item.PartitionDescriptions ?? item.PartitionIDs ?? EMPTY_STRING

                        return {
                            key: buildAdminListRowKey(
                                [listCmeId, entryId, item.DateTime ?? EMPTY_STRING],
                                pageIdx * rowsPerPage,
                                index
                            ),
                            entryId,
                            Description: item.Description ?? EMPTY_STRING,
                            DateTime: item.DateTime ?? EMPTY_STRING,
                            User: item.UserID ?? EMPTY_STRING,
                            Action: item.Action ?? EMPTY_STRING,
                            Type: item.TableName ?? EMPTY_STRING,
                            CMEnvironment: item.CMEnvironmentDesc ?? item.CMEnvironmentID ?? EMPTY_STRING,
                            TelSys: configChangeLogTelSysOrPartitionDisplay(telSysRaw, noneScopeLabel),
                            Partition: configChangeLogTelSysOrPartitionDisplay(partitionRaw, noneScopeLabel)
                        }
                    }
                )

                itemsByEntryIdRef.current = nextById
                setRows(mapped)
            })
            .catch(displayError)
            .finally((): void => {
                if (!isMounted.current) return

                setLoading((currentLoading: number): number => currentLoading - 1)
                setHasCompletedInitialListFetch(true)
            })
    }, [
        hasReceivedInitialFilters,
        listCmeId,
        listIncludeRecursive,
        listPartitionIdsParam,
        listTelSysIdsParam,
        pagination,
        includeActionsKey,
        includeTypesKey,
        listBefore,
        searchTrim,
        sort,
        suppressNulls,
        setLoading,
        setTotalCount,
        language
    ])

    useEffect((): void => {
        getLogList()

        return undefined
    }, [getLogList])

    const resetPaginationToFirstPage = useCallback((): void => {
        lastMetaRef.current = null
        prevPageIdxRef.current = 0
        setPagination((current: PaginationInfo): PaginationInfo => [0, current[1] ?? DEFAULT_ROWS_PER_PAGE])
    }, [setPagination])

    const openDetailForRows = useCallback((selected: ConfigChangeLogRow[]): void => {
        const entryId: string = selected[0]?.entryId ?? EMPTY_STRING
        const row: Definable<ConfigChangeLogListItem> = isStringNotEmpty(entryId)
            ? itemsByEntryIdRef.current.get(entryId)
            : undefined

        if (IsVarEmpty(row)) return

        setDetailRow(row)
        setDetailOpen(true)
    }, [])

    const handleToolbarViewClick = useCallback(
        (selectedRows: ConfigChangeLogRow[]): void => {
            openDetailForRows(selectedRows)
        },
        [openDetailForRows]
    )

    const handleToolbarChangeClick = useCallback(
        (selectedRows: ConfigChangeLogRow[]): void => {
            openDetailForRows(selectedRows)
        },
        [openDetailForRows]
    )

    const columns: DataTableColumn<ConfigChangeLogRow, keyof ConfigChangeLogRow>[] = useMemo(
        (): DataTableColumn<ConfigChangeLogRow, keyof ConfigChangeLogRow>[] => [
            {
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_DateTime),
                field: 'DateTime',
                key: 'changelog-datetime',
                sortable: true,
                serverSortField: 'DateTime'
            },
            {
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_Description),
                field: 'Description',
                key: 'changelog-description',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_User),
                field: 'User',
                key: 'changelog-user',
                sortable: false
            },
            {
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_Action),
                field: 'Action',
                key: 'changelog-action',
                sortable: false
            },
            {
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_Type),
                field: 'Type',
                key: 'changelog-type',
                sortable: false
            },
            {
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_CME),
                field: 'CMEnvironment',
                key: 'changelog-cme',
                sortable: false,
                hideBelow: 'md'
            },
            {
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_TelSys),
                field: 'TelSys',
                key: 'changelog-telsys',
                sortable: false,
                hideBelow: 'md'
            },
            {
                headerName: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Column_Partition),
                field: 'Partition',
                key: 'changelog-partition',
                sortable: false,
                hideBelow: 'md'
            }
        ],
        [language]
    )

    const actionLabel = useCallback(
        (action: string): string => {
            if (action === EMPTY_STRING) return language.get(CONFIG_CHANGE_LOG_ACTION_DESCRIPTION_ALL_KEY)

            const bundleKey: Definable<string> = CONFIG_CHANGE_LOG_ACTION_DESCRIPTION_KEY_BY_VALUE[action]

            if (isVarNotEmpty(bundleKey)) return language.get(bundleKey)

            return action
        },
        [language]
    )

    const entityTypeLabel = useCallback(
        (entityType: string): string => {
            if (entityType === EMPTY_STRING) return language.get(CONFIG_CHANGE_LOG_TYPE_DESCRIPTION_ALL_KEY)

            const bundleKey: Definable<string> = CONFIG_CHANGE_LOG_TYPE_DESCRIPTION_KEY_BY_VALUE[entityType]

            if (isVarNotEmpty(bundleKey)) return language.get(bundleKey)

            return entityType
        },
        [language]
    )

    const renderChangeLogAdditionalFilters = useCallback(
        (args: AdminFiltersSlotRenderArgs): ReactElement => (
            <ConfigChangeLogListAdditionalFilters
                {...args}
                changeLogBeforeLabel={language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Filter_Before)}
                changeLogActionValues={CONFIG_CHANGE_LOG_ACTION_VALUES}
                changeLogTypeValues={CONFIG_CHANGE_LOG_ENTITY_TYPE_VALUES}
                changeLogActionOptionLabel={actionLabel}
                changeLogTypeOptionLabel={entityTypeLabel}
                changeLogActionsLabel={language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Filter_Actions)}
                changeLogTypesLabel={language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Filter_EntityTypes)}
                changeLogSuppressNullsLabel={language.get(
                    ConfigChangeLogListLanguage.ConfigChangeLogList_Filter_SuppressNulls
                )}
                changeLogSuppressNullsNoLabel={language.get(
                    ConfigChangeLogListLanguage.ConfigChangeLogList_Filter_SuppressNulls_No
                )}
                changeLogSuppressNullsYesLabel={language.get(
                    ConfigChangeLogListLanguage.ConfigChangeLogList_Filter_SuppressNulls_Yes
                )}
                formatChangeLogMultiSelectSelectionLabel={(count: number): string =>
                    language.getInterpolated(
                        ConfigChangeLogListLanguage.ConfigChangeLogList_Filter_MultiSelectedCount,
                        count
                    )
                }
            />
        ),
        [actionLabel, entityTypeLabel, language]
    )

    const isChangeLogTableLoading: boolean =
        loading > 0 ||
        !hasReceivedInitialFilters ||
        (hasReceivedInitialFilters && isStringNotEmpty(listCmeId) && !hasCompletedInitialListFetch)

    return (
        <div className='config-change-log-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_auditTrail} />
            <div className='config-change-log-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(ConfigChangeLogListLanguage.ConfigChangeLogList_Filter_SearchUsername)
                    }}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialAdminScopeFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }

                        resetPaginationToFirstPage()
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        includeCmeChildren: true,
                        includeRefresh: true,
                        search: true
                    }}
                    additionalFilterSkeletonCount={4}
                    additionalFilters={renderChangeLogAdditionalFilters}
                    onRefresh={getLogList}
                />
                <DataTable<ConfigChangeLogRow, keyof ConfigChangeLogRow>
                    permissionName={AdminBackendPermissionNames.AUDIT_TRAIL}
                    localeText={{
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={rows}
                    defaultPageNumber={pagination[0]}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={isChangeLogTableLoading}
                    paginationServer
                    sortServer
                    toolbarDisabled
                    isSelectable={false}
                    colIndexToTriggerClick={0}
                    onToolbarViewClick={handleToolbarViewClick}
                    onToolbarChangeClick={handleToolbarChangeClick}
                    onSortChange={(nextSort: Definable<string>): void => {
                        setSort(nextSort ?? CONFIG_CHANGE_LOG_DEFAULT_SORT)
                        resetPaginationToFirstPage()
                    }}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void => {
                        lastMetaRef.current = null
                        prevPageIdxRef.current = 0
                        setPagination([currentPage, newRowsPerPage])
                    }}
                    onPageChange={(newPage: number, rowsPerPage: number): void => {
                        setPagination([newPage, rowsPerPage])
                    }}
                />
                {detailOpen && isVarNotEmpty(detailRow) ? (
                    <ConfigChangeLogDetailModal
                        key={detailRow.EntryID ?? 'config-change-log-detail'}
                        row={detailRow}
                        onModalExited={(): void => {
                            setDetailOpen(false)
                            setDetailRow(null)
                        }}
                    />
                ) : null}
            </div>
        </div>
    )
}
